import React, { useState, useEffect } from 'react';
import { sliderService } from '../../../services/sliderService';
import { useAuth } from '../../Admin/context/AuthContext';   // ✅ sesuaikan path
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaSave,
    FaCheckCircle, FaExclamationTriangle, FaEye, FaEyeSlash,
    FaImage, FaArrowUp, FaArrowDown, FaExternalLinkAlt
} from 'react-icons/fa';

const ManajemenSlider = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [gambarFile, setGambarFile] = useState(null);
    const [gambarPreview, setGambarPreview] = useState(null);

    // ✅ Ambil user dari AuthContext
    const { user } = useAuth();

    // ✅ Helper cek permission
    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    const [formData, setFormData] = useState({
        judul: '',
        deskripsi: '',
        link: '',
        link_text: 'Selengkapnya',
        link_secondary: '',
        link_secondary_text: 'Layanan Kami',
        order: 0,
        is_active: true,
    });

    const loadData = async () => {
        setLoading(true);
        const params = {};
        if (searchTerm) params.search = searchTerm;
        if (filterActive !== '') params.is_active = filterActive;

        const result = await sliderService.getList(params);
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            judul: '',
            deskripsi: '',
            link: '',
            link_text: 'Selengkapnya',
            link_secondary: '',
            link_secondary_text: 'Layanan Kami',
            order: dataList.length + 1,
            is_active: true,
        });
        setGambarFile(null);
        setGambarPreview(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            judul: item.judul || '',
            deskripsi: item.deskripsi || '',
            link: item.link || '',
            link_text: item.link_text || 'Selengkapnya',
            link_secondary: item.link_secondary || '',
            link_secondary_text: item.link_secondary_text || 'Layanan Kami',
            order: item.order || 0,
            is_active: item.is_active === true || item.is_active === 1,
        });
        setGambarFile(null);
        setGambarPreview(item.gambar ? getImageUrl(item.gambar) : null);
        setShowForm(true);
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleGambarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGambarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setGambarPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!editing && !gambarFile) {
            showNotif('Gambar wajib diupload', 'error');
            return;
        }

        setSubmitting(true);

        const submitData = new FormData();
        submitData.append('judul', formData.judul);
        submitData.append('deskripsi', formData.deskripsi || '');
        submitData.append('link', formData.link || '');
        submitData.append('link_text', formData.link_text || 'Selengkapnya');
        submitData.append('link_secondary', formData.link_secondary || '');
        submitData.append('link_secondary_text', formData.link_secondary_text || 'Layanan Kami');
        submitData.append('order', formData.order || 0);
        submitData.append('is_active', formData.is_active ? 1 : 0);

        if (gambarFile) {
            submitData.append('gambar', gambarFile);
        }

        let result;
        if (editing) {
            result = await sliderService.update(editing.id, submitData);
        } else {
            result = await sliderService.create(submitData);
        }

        if (result.success) {
            showNotif(result.message || 'Slider berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await sliderService.delete(deleteConfirm.id);
        if (result.success) {
            showNotif('Slider berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleToggleActive = async (item) => {
        const result = await sliderService.toggleActive(item.id);
        if (result.success) {
            showNotif('Status berhasil diubah');
            loadData();
        }
    };

    const handleMoveUp = async (index) => {
        if (index === 0) return;
        const newList = [...dataList];
        const temp = newList[index].order;
        newList[index].order = newList[index - 1].order;
        newList[index - 1].order = temp;

        const items = newList.map((item, i) => ({ id: item.id, order: i + 1 }));
        const result = await sliderService.reorder(items);
        if (result.success) {
            loadData();
        }
    };

    const handleMoveDown = async (index) => {
        if (index === dataList.length - 1) return;
        const newList = [...dataList];
        const temp = newList[index].order;
        newList[index].order = newList[index + 1].order;
        newList[index + 1].order = temp;

        const items = newList.map((item, i) => ({ id: item.id, order: i + 1 }));
        const result = await sliderService.reorder(items);
        if (result.success) {
            loadData();
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Slider</h2>
                    <p className="text-gray-600">Kelola gambar slideshow di halaman utama</p>
                </div>
                {/* ✅ Tombol Tambah — cek permission */}
                {hasPermission('slider.create') && (
                    <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                        <FaPlus className="mr-2" /> Tambah Slider
                    </button>
                )}
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari judul slider..."
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
                <select
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                    className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="">Semua Status</option>
                    <option value="1">Aktif</option>
                    <option value="0">Nonaktif</option>
                </select>
                <button onClick={loadData} className="btn-primary">Filter</button>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2E7D32] mx-auto"></div>
                </div>
            ) : dataList.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                    <FaImage className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-500 mb-2">Belum Ada Slider</h3>
                    <p className="text-gray-400 mb-6">Tambahkan slider untuk ditampilkan di homepage</p>
                    {/* ✅ Tombol Tambah Slider Pertama — cek permission */}
                    {hasPermission('slider.create') && (
                        <button onClick={handleCreate} className="btn-primary">
                            <FaPlus className="mr-2 inline" /> Tambah Slider Pertama
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dataList.map((item, index) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow">
                            {/* Image Preview */}
                            <div className="relative h-48 bg-gray-100">
                                {item.gambar ? (
                                    <img
                                        src={getImageUrl(item.gambar)}
                                        alt={item.judul}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <FaImage className="text-4xl text-gray-300" />
                                    </div>
                                )}

                                {/* Order Badge */}
                                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                                    #{item.order}
                                </div>

                                {/* Status Badge — cek permission untuk toggle */}
                                <div className="absolute top-2 right-2">
                                    {hasPermission('slider.update') ? (
                                        <button
                                            onClick={() => handleToggleActive(item)}
                                            className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${item.is_active
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-400 text-white'
                                                }`}
                                        >
                                            {item.is_active ? <FaEye size={10} /> : <FaEyeSlash size={10} />}
                                            {item.is_active ? 'Aktif' : 'Nonaktif'}
                                        </button>
                                    ) : (
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${item.is_active
                                            ? 'bg-green-500 text-white'
                                            : 'bg-gray-400 text-white'
                                            }`}>
                                            {item.is_active ? <FaEye size={10} /> : <FaEyeSlash size={10} />}
                                            {item.is_active ? 'Aktif' : 'Nonaktif'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-4">
                                <h3 className="font-bold text-[#0F4C3A] mb-1 line-clamp-2">{item.judul}</h3>
                                {item.deskripsi && (
                                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{item.deskripsi}</p>
                                )}

                                {/* Link Info */}
                                {item.link && (
                                    <div className="flex items-center gap-1 text-xs text-blue-500 mb-3">
                                        <FaExternalLinkAlt size={10} />
                                        <span className="truncate">{item.link}</span>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex justify-between items-center pt-3 border-t">
                                    {/* Reorder — cek permission */}
                                    <div className="flex gap-1">
                                        {hasPermission('slider.update') && (
                                            <>
                                                <button
                                                    onClick={() => handleMoveUp(index)}
                                                    disabled={index === 0}
                                                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-30"
                                                    title="Naikkan urutan"
                                                >
                                                    <FaArrowUp size={12} />
                                                </button>
                                                <button
                                                    onClick={() => handleMoveDown(index)}
                                                    disabled={index === dataList.length - 1}
                                                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-30"
                                                    title="Turunkan urutan"
                                                >
                                                    <FaArrowDown size={12} />
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    <div className="flex gap-1">
                                        {/* ✅ Tombol Edit — cek permission */}
                                        {hasPermission('slider.update') && (
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                title="Edit"
                                            >
                                                <FaEdit size={14} />
                                            </button>
                                        )}

                                        {/* ✅ Tombol Hapus — cek permission */}
                                        {hasPermission('slider.delete') && (
                                            <button
                                                onClick={() => setDeleteConfirm(item)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                title="Hapus"
                                            >
                                                <FaTrash size={14} />
                                            </button>
                                        )}

                                        {/* Fallback */}
                                        {!hasPermission('slider.update') && !hasPermission('slider.delete') && (
                                            <span className="text-xs text-gray-400 italic">-</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">
                                {editing ? 'Edit Slider' : 'Tambah Slider'}
                            </h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Judul <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="judul"
                                        value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Judul slider"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Deskripsi
                                    </label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Deskripsi singkat..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <FaImage className="inline mr-1" /> Gambar Slider{' '}
                                        {!editing && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleGambarChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Format: JPG, PNG, WebP. Maks: 5MB. Rekomendasi: 1920x600px
                                    </p>
                                    {gambarPreview && (
                                        <div className="mt-3">
                                            <p className="text-xs text-gray-500 mb-1">Preview:</p>
                                            <img
                                                src={gambarPreview}
                                                alt="Preview"
                                                className="w-full max-h-48 object-cover rounded-lg border"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Link Tombol Utama
                                        </label>
                                        <input
                                            type="text"
                                            name="link"
                                            value={formData.link}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="/layanan atau https://..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Teks Tombol
                                        </label>
                                        <input
                                            type="text"
                                            name="link_text"
                                            value={formData.link_text}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Selengkapnya"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Link Tombol Kedua (Opsional)
                                        </label>
                                        <input
                                            type="text"
                                            name="link_secondary"
                                            value={formData.link_secondary}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="/layanan"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Teks Tombol
                                        </label>
                                        <input
                                            type="text"
                                            name="link_secondary_text"
                                            value={formData.link_secondary_text}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Layanan Kami"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Urutan
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            min="0"
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer w-full">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                                className="mr-2"
                                            />
                                            <span className="text-sm font-medium">Aktifkan Slider</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Slider?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.judul}</strong>
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenSlider;